import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin } from 'lucide-react';

export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-gray-950 text-gray-400">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <img src="/kipikid-logo.png" alt="KipiKid" className="w-14 h-14" />
              <span className="text-lg font-bold text-white">KipiKid</span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed max-w-[260px]">
              {t('footer.description')}
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white text-sm font-bold mb-4 uppercase tracking-wider">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2.5">
              <li><Link to="/parents" className="text-sm hover:text-cyan-400 transition-colors">{t('nav.parents')}</Link></li>
              <li><Link to="/kindergartens" className="text-sm hover:text-cyan-400 transition-colors">{t('nav.kindergartens')}</Link></li>
              <li><Link to="/networks" className="text-sm hover:text-cyan-400 transition-colors">{t('nav.networks')}</Link></li>
              <li><Link to="/emergency" className="text-sm hover:text-cyan-400 transition-colors">{t('nav.emergency')}</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-sm font-bold mb-4 uppercase tracking-wider">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-3.5 h-3.5 text-cyan-500" aria-hidden="true" />
                </div>
                <span dir="ltr">050-5774499</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-3.5 h-3.5 text-cyan-500" aria-hidden="true" />
                </div>
                <span>info@kipikid.com</span>
              </li>
              <li className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-3.5 h-3.5 text-cyan-500" aria-hidden="true" />
                </div>
                <span>חיים הרצוג 23, ראש העין</span>
              </li>
            </ul>
          </div>

          {/* Social & Legal */}
          <div>
            <h3 className="text-white text-sm font-bold mb-4 uppercase tracking-wider">{t('footer.followUs')}</h3>
            <div className="flex gap-2.5 mb-6">
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-cyan-600 hover:text-white transition-all duration-200">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-cyan-600 hover:text-white transition-all duration-200">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-gray-500 hover:bg-cyan-600 hover:text-white transition-all duration-200">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
            <ul className="space-y-2">
              <li><Link to="/privacy" className="text-sm hover:text-cyan-400 transition-colors">{t('footer.privacy')}</Link></li>
              <li><Link to="/terms" className="text-sm hover:text-cyan-400 transition-colors">{t('footer.terms')}</Link></li>
              <li><Link to="/accessibility-statement" className="text-sm hover:text-cyan-400 transition-colors">{t('footer.accessibility')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-8 border-t border-gray-800/60 text-center text-sm text-gray-600">
          &copy; {year} KipiKid. {t('footer.rights')}.
        </div>
      </div>
    </footer>
  );
}
